import React, { useCallback, useEffect, useRef, useState } from "react";
import { IEventSummary, IScore, IShow } from "@eagerdog/interfaces";
import { CONFORMATION_SCORE_UPDATED_ID, preferredShowIds } from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { scoreSocketService } from "src/services/scoreSocket.service";
import { toast } from "src/components/Toast/ToastManager";

import { BestInShowPanel, IShowStream } from "./BestInShowPanel";
import { BreedsPanel } from "./BreedsPanel";
import { GroupsPanel } from "./GroupsPanel";
import JuniorShowmanshipPanel from "./JuniorShowmanshipPanel";
import { partitionScores } from "./partitionScores";

import styles from "./UKCConformationResults.module.scss";

interface IProps {
  event: IEventSummary;
  show: IShow;
}

const preferredIds = (show: IShow, kind: IShowStream["id"]): string[] =>
  preferredShowIds(show, kind);

const UKCConformationResults: React.FC<IProps> = ({ event, show }) => {
  const [scores, setScores] = useState<IScore[]>([]);
  const [showData, setShowData] = useState<IShow>(show);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const result = await apiService.getConformationEntrySummary(event._id, show._id);
      setScores(result.scores);
      setShowData(result.show);
    } catch (error) {
      toast.show({
        title: "Error",
        content: "Failed to load conformation entries.",
        type: "fail",
        errorDetails: error as AxiosError,
      });
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [event._id, show._id]);

  useEffect(() => {
    load(true);
  }, [load]);

  // Saves from other devices (mobile Scoring tab, another browser) arrive on the
  // /scores room with the conformation sentinel id — refresh silently.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);
  useEffect(() => {
    const showId = String(show._id);
    scoreSocketService.joinShowRoom(showId);
    const unsubscribe = scoreSocketService.onScoreUpdated((data) => {
      if (data.showId === showId && data.scoreId === CONFORMATION_SCORE_UPDATED_ID) {
        loadRef.current();
      }
    });
    return () => {
      unsubscribe();
      scoreSocketService.leaveShowRoom(showId);
    };
  }, [show._id]);

  if (loading) {
    return <div className={styles.loading}>Loading entries…</div>;
  }

  const parts = partitionScores(scores);
  const showStreams: IShowStream[] = [
    { id: "regular", title: "Regular Conformation", scores: parts.regular, preferred: preferredIds(showData, "regular") },
    { id: "altered", title: "Altered Conformation", scores: parts.altered, preferred: preferredIds(showData, "altered") },
    { id: "novice_puppy", title: "Novice Puppy", scores: parts.novicePuppy, preferred: preferredIds(showData, "novice_puppy") },
    { id: "veteran", title: "Veteran", scores: parts.veteran, preferred: preferredIds(showData, "veteran") },
    { id: "brace", title: "Brace", scores: parts.brace, preferred: preferredIds(showData, "brace") },
  ];

  if (!scores.length) {
    return <div className={styles.emptyBreed}>No conformation entries for this show.</div>;
  }

  return (
    <div className={styles.UKCConformationResults}>
      <JuniorShowmanshipPanel scores={parts.junior} event={event} show={showData} onSaved={() => load()} />
      <BestInShowPanel streams={showStreams} event={event} show={showData} onSaved={() => load()} />
      <GroupsPanel scores={parts.regular} event={event} show={showData} onSaved={() => load()} />
      <BreedsPanel
        event={event}
        show={showData}
        onSaved={() => load()}
        streams={[
          { id: "regular", title: "Regular Conformation", scores: parts.regular },
          { id: "altered", title: "Altered Conformation", scores: parts.altered },
          { id: "novice_puppy", title: "Novice Puppy", scores: parts.novicePuppy },
          { id: "veteran", title: "Veteran", scores: parts.veteran },
          { id: "brace", title: "Brace", scores: parts.brace },
        ]}
      />
    </div>
  );
};

export default UKCConformationResults;
